import React, { useState } from 'react';
import {
    Button,
    InputTextArea,
    PopoverShell,
    SparkIcon,
    Spinner,
    Text,
    TooltipTrigger,
} from '@spark';

import SampleInputStep from './SampleInputStep';
import ModelOutputStep from './ModelOutputStep';
import ResponseTypeSelector from './ResponseTypeSelector';
import WhenEvaluatingSection from './WhenEvaluatingSection';
import { getValuesByPath } from './utils';
import ValidationAlert from './ValidationAlert';
import { getParserSchema } from './parserSchemas';
import { useResponseMappingState } from '@/features/model-setup/hooks/useResponseMappingState';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';

// Hover-triggered rich-content popover (replaces antd Tooltip with JSX title).
const HoverPopover = ({ content, children }) => {
    const [open, setOpen] = useState(false);
    return (
        <span
            style={{ position: 'relative', display: 'inline-flex' }}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            {children}
            {open && (
                <div style={{ position: 'absolute', left: 0, top: 'calc(100% + 4px)', zIndex: 1400 }}>
                    <PopoverShell tip={false}>
                        <div style={{ padding: 16, maxWidth: 520 }}>
                            {content}
                        </div>
                    </PopoverShell>
                </div>
            )}
        </span>
    );
};

/**
 * Response interpreter body shown when compare-to-commercial is on.
 * Mapping mechanics unchanged. Toolbar holds Response type + Inspect logic;
 * Shuffle Example is on Sample Input and Run is on Model Returns. Pass
 * hideToolbar to drop the toolbar (re-eval modal) while keeping card actions.
 * Pass mappingReadOnly to freeze parser-field picks while leaving Run enabled.
 */
const ResponseInterpreterSection = ({
    requestExample,
    setRequestExample,
    onShuffleExample,
    shuffleLoading,
    responseType,
    setResponseType,
    rawResponse,
    onGenerateResponse,
    generatingResponse,
    runValidationError,
    onInspectLogic,
    extraParamsOptions,
    extraParamsValues,
    onExtraParamsValuesChange,
    promptExtraParams,
    rawResponseLoading,
    rawResponseError,
    validationResult,
    taskType,
    isMultilabel,
    onParserPrefillChange,
    initialParserSelections = {},
    userCode,
    onUserCodeChange,
    onValidate,
    validationLoading,
    hideToolbar = false,
    mappingReadOnly = false,
    batchNotice = null,
    // Prompt template for the Input card's "What the model receives" tab
    // (tokens resolved against the current sample). Optional — hosts without
    // a prompt (re-eval modal) keep the single Example view.
    promptTemplate = '',
    // "When evaluating" cleanup flags (Model Setup only; null hides the section).
    normalization = null,
    onNormalizationChange = null,
    normalizationDisabled = false,
    labelNames = null,
}) => {
    const {
        parsedJson,
        jsonParseError,
        handleResponseTypeChange,
        showJsonResponseTypeOption,
        showHighlightInstruction,
        isNerJsonMode,
        selectedJsonTokens,
        nerPathAssignments,
        toggleJsonSelection,
        handleRemoveNerAssignment,
        buildNerDropdownMenu,
        jsonFieldMetaMap,
        getLabelFromFieldPath,
        normalizedJsonLabelField,
        jsonLabelFieldOptions,
        handleLabelFieldChange,
        multilabelDelimiter,
        setMultilabelDelimiter,
        singleLabelListMode,
        setSingleLabelListMode,
        getJsonValueByPath,
        formatNerAssignedValue,
        splitLabels,
        delimiterOptions,
        handleManualNerAssignment,
        jsonFieldSelectOptions,
        entityFieldConfig,
        relationFieldConfig,
        supportsRelationFields,
        nerFieldAssignments,
    } = useResponseMappingState({
        taskType,
        isMultilabel,
        responseType,
        setResponseType,
        rawResponse,
        initialParserSelections,
        onParserPrefillChange,
        onUserCodeChange,
    });

    // Extracted value the "Evaluated as" preview chip cleans up. NER has no
    // single extracted string, and custom mode is judged by the parser.
    const evaluatedSampleValue = () => {
        if (taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS) {
            return null;
        }
        if (responseType === 'text') {
            return rawResponse || null;
        }
        if (responseType !== 'json' || !parsedJson) {
            return null;
        }
        if (normalizedJsonLabelField != null) {
            const value = formatNerAssignedValue(
                getJsonValueByPath(parsedJson, normalizedJsonLabelField)
            );
            return value || null;
        }
        if (isGenerativeTask(taskType) && taskType === TaskType.contentGeneration) {
            return formatNerAssignedValue(parsedJson) || null;
        }
        return null;
    };

    const renderCustomMode = () => {
        const schemaSpec = getParserSchema(taskType, isMultilabel);

        const schemaContent = (
            <div style={{ width: 500, maxWidth: '100%' }}>
                <Text className="fs-unmask" variant="heading-5-semibold" as="h5" style={{ marginTop: 0 }}>
                    {schemaSpec.title}
                </Text>
                <pre
                    className="fs-unmask"
                    style={{
                        background: '#fafafa',
                        borderRadius: 8,
                        padding: 12,
                        border: '1px solid #e4e4e4',
                        fontFamily: 'JetBrains Mono, Monaco, monospace',
                        fontSize: 13,
                        whiteSpace: 'pre-wrap',
                        marginBottom: 12,
                        color: '#898989',
                    }}
                >
                    {schemaSpec.schema}
                </pre>
                <div
                    className="fs-unmask"
                    style={{
                        background: '#f0f6ff',
                        border: '1px solid #c7deff',
                        color: '#898989',
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginTop: 10,
                        padding: '8px 12px',
                        borderRadius: 4,
                    }}
                >
                    <span style={{ color: '#1677ff', marginTop: 4, marginRight: 8, display: 'inline-flex' }}>
                        <SparkIcon slug="circle-info" size="small" />
                    </span>
                    <div style={{ font: 'Inter', fontSize: 13 }}>{schemaSpec.info}</div>
                </div>
            </div>
        );

        return (
            <div className="response-interpreter__columns">
                <div className="response-interpreter__column">
                    <Text className="fs-unmask" variant="lead-semibold" color="primary" as="div">
                        Raw model output
                    </Text>
                    <Text className="fs-unmask" variant="base-regular" color="secondary" as="div">
                        The raw output before parsing.
                    </Text>
                    <pre className="optune-scrollbar response-interpreter__raw-pre">
                        {rawResponse}
                    </pre>
                </div>
                <div className="response-interpreter__column">
                    <div className="response-interpreter__logic-heading fs-unmask">
                        <Text variant="lead-semibold" color="primary" as="div">
                            Logic
                        </Text>
                        <HoverPopover content={schemaContent}>
                            <span style={{ color: '#1677ff', display: 'inline-flex' }}>
                                <SparkIcon slug="circle-help" size="small" />
                            </span>
                        </HoverPopover>
                    </div>
                    <div className="response-interpreter__logic-editor">
                        <InputTextArea
                            showLabel={false}
                            value={userCode || ''}
                            onChange={(e) => onUserCodeChange && onUserCodeChange(e.target.value)}
                            rows={10}
                            style={{ fontFamily: 'monospace', border: 'none', padding: 0 }}
                            placeholder="# Implement your logic here to return the expected output"
                            disabled={mappingReadOnly}
                        />
                    </div>
                    <div className="response-interpreter__logic-actions">
                        <TooltipTrigger text="Check if the logic returns the expected output">
                            <Button
                                variant="secondary"
                                onClick={onValidate}
                                startIcon={validationLoading ? <Spinner size="small" /> : undefined}
                                disabled={validationLoading}
                                disableWhenReadOnly
                                className="blue-button"
                            >
                                Validate
                            </Button>
                        </TooltipTrigger>
                    </div>
                    {validationResult && (
                        <ValidationAlert
                            validationResult={validationResult}
                            taskType={taskType}
                            isMultilabel={isMultilabel}
                        />
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="response-interpreter">
            {!hideToolbar && responseType === 'custom' && (
                <div className="response-interpreter__toolbar fs-unmask">
                    <div className="response-interpreter__toolbar-left">
                        <ResponseTypeSelector
                            taskType={taskType}
                            responseType={responseType}
                            onChange={handleResponseTypeChange}
                            variant="toolbar"
                            showJsonOption={showJsonResponseTypeOption}
                            disabled={mappingReadOnly}
                        />
                    </div>
                    <div className="response-interpreter__toolbar-right">
                        <Button
                            type="button"
                            variant="ghost"
                            size="small"
                            onClick={onInspectLogic}
                            className="response-step-card__inspect-logic"
                        >
                            Inspect logic
                        </Button>
                    </div>
                </div>
            )}

            <div className="response-interpreter__flow" style={{ position: 'relative' }}>
                {(rawResponseLoading || generatingResponse) && (
                    <div className="response-interpreter__loading-overlay">
                        <Spinner size="large" />
                    </div>
                )}
                {responseType === 'custom' && !hideToolbar ? (
                    renderCustomMode()
                ) : (
                    <div className="response-interpreter__columns">
                        <SampleInputStep
                            requestExample={requestExample}
                            setRequestExample={setRequestExample}
                            onShuffleExample={onShuffleExample}
                            shuffleLoading={shuffleLoading}
                            extraParamsValues={extraParamsValues}
                            generatingResponse={generatingResponse}
                            rawResponseLoading={rawResponseLoading}
                            promptTemplate={promptTemplate}
                        />
                        <div className="response-interpreter__arrow fs-unmask" aria-hidden="true">
                            <SparkIcon slug="arrow-right" size="small" />
                        </div>
                        <ModelOutputStep
                            onInspectLogic={onInspectLogic}
                            onGenerateResponse={onGenerateResponse}
                            generatingResponse={generatingResponse}
                            runValidationError={runValidationError}
                            rawResponseError={rawResponseError}
                            responseType={responseType}
                            rawResponse={rawResponse}
                            jsonParseError={jsonParseError}
                            parsedJson={parsedJson}
                            taskType={taskType}
                            isMultilabel={isMultilabel}
                            showHighlightInstruction={showHighlightInstruction}
                            isNerJsonMode={isNerJsonMode}
                            selectedJsonTokens={selectedJsonTokens}
                            nerPathAssignments={nerPathAssignments}
                            toggleJsonSelection={mappingReadOnly ? () => { } : toggleJsonSelection}
                            handleRemoveNerAssignment={mappingReadOnly ? () => { } : handleRemoveNerAssignment}
                            buildNerDropdownMenu={buildNerDropdownMenu}
                            jsonFieldMetaMap={jsonFieldMetaMap}
                            getLabelFromFieldPath={getLabelFromFieldPath}
                            normalizedJsonLabelField={normalizedJsonLabelField}
                            jsonLabelFieldOptions={jsonLabelFieldOptions}
                            setSelectedJsonLabelField={mappingReadOnly ? () => { } : handleLabelFieldChange}
                            multilabelDelimiter={multilabelDelimiter}
                            setMultilabelDelimiter={mappingReadOnly ? () => { } : setMultilabelDelimiter}
                            singleLabelListMode={singleLabelListMode}
                            setSingleLabelListMode={mappingReadOnly ? () => { } : setSingleLabelListMode}
                            getJsonValueByPath={getJsonValueByPath}
                            formatNerAssignedValue={formatNerAssignedValue}
                            splitLabels={splitLabels}
                            delimiterOptions={delimiterOptions}
                            getValuesByPath={getValuesByPath}
                            initialParserSelections={initialParserSelections}
                            handleManualNerAssignment={mappingReadOnly ? () => { } : handleManualNerAssignment}
                            jsonFieldSelectOptions={jsonFieldSelectOptions}
                            entityFieldConfig={entityFieldConfig}
                            relationFieldConfig={relationFieldConfig}
                            supportsRelationFields={supportsRelationFields}
                            nerFieldAssignments={nerFieldAssignments}
                            hideStepBadge
                            hideInspectLogic={hideToolbar}
                            mappingReadOnly={mappingReadOnly}
                            batchNotice={batchNotice}
                            showResponseTypeControl={!hideToolbar}
                            onResponseTypeChange={handleResponseTypeChange}
                            showJsonResponseTypeOption={showJsonResponseTypeOption}
                        />
                    </div>
                )}
            </div>

            {normalization && onNormalizationChange && (
                <WhenEvaluatingSection
                    normalization={normalization}
                    onChange={onNormalizationChange}
                    disabled={normalizationDisabled}
                    evaluatedSample={evaluatedSampleValue()}
                    labelNames={labelNames}
                />
            )}
        </div>
    );
};

export default ResponseInterpreterSection;
